import { useState, useMemo } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Census } from "@/lib/api";

const SAMPLE = `Room\tPatient\tAdmission\tStatus
305-A\tEdman, Cassandra\t7/1/26\tVOL
203-B\tBroitzman, Jakara\t7/11/26\tVOL`;

export default function PasteCensus({ value, onChange }) {
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);

  const runPreview = async (text) => {
    if (!text.trim()) {
      setPreview([]);
      return;
    }
    setBusy(true);
    try {
      const res = await Census.parse(text);
      setPreview(res.rows || []);
    } catch {
      setPreview([]);
    } finally {
      setBusy(false);
    }
  };

  const handleChange = (v) => {
    onChange(v);
    runPreview(v);
  };

  const rowCount = preview.length;

  return (
    <div data-testid="paste-census-component">
      <div
        className={[
          "w-full rounded-xl border-2 border-dashed transition-colors",
          value ? "border-[#0284c7] bg-[#f6f9f8]" : "border-[#c4c4c0] bg-[#f1f5f9]",
        ].join(" ")}
      >
        <Textarea
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={`Paste from Excel (Tab), CSV, or | delimited. Example:\n\n${SAMPLE}`}
          className="min-h-[180px] font-mono text-sm border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 resize-y placeholder:text-[#909590]"
          data-testid="paste-census-input"
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-[#475569]">
        <span data-testid="paste-census-count">
          {busy ? "Parsing…" : rowCount ? `${rowCount} patient${rowCount !== 1 ? "s" : ""} detected` : "Paste to preview"}
        </span>
        {value && (
          <button
            type="button"
            className="text-[#b94a48] hover:underline"
            onClick={() => handleChange("")}
            data-testid="clear-census-button"
          >
            Clear
          </button>
        )}
      </div>

      {preview.length > 0 && (
        <div className="mt-3 border border-[#e2e8f0] rounded-lg overflow-hidden bg-white" data-testid="paste-preview">
          <div className="max-h-[220px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f1f5f9] sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Patient</th>
                  <th className="px-3 py-2 font-medium">Room</th>
                  <th className="px-3 py-2 font-medium">Admit (LOS)</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium" title="This column is parsed but IGNORED during Generate — continuity + unit preferences drive assignment.">
                    Provider <span className="text-[10px] text-[#909590] font-normal">(ignored)</span>
                  </th>
                  <th className="px-3 py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 40).map((p, i) => (
                  <tr key={i} className="border-t border-[#e2e8f0]">
                    <td className="px-3 py-1.5">{p.name}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{p.room || ""}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{p.admission_raw || p.admission_date || ""}</td>
                    <td className="px-3 py-1.5">{p.status || ""}</td>
                    <td
                      className={[
                        "px-3 py-1.5",
                        p.provider_name ? "text-[#0284c7] font-medium" : "text-[#c4c4c0] italic",
                      ].join(" ")}
                    >
                      {p.provider_name || "—"}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-[#475569]">{p.note || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.length > 40 && (
            <div className="px-3 py-2 text-xs text-[#475569] bg-[#f8fafc] border-t border-[#e2e8f0]">
              …plus {preview.length - 40} more.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
